import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { SolutionsAppService } from 'src/app/services/solutions-app.service';

/**
 * "Create a new version" dialog.
 * Closes with 'New' (empty version) or a version_id string (copy), or false.
 */
@Component({
  selector: 'app-new-version-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  template: `
    <div class="nvd-head">
      <div class="nvd-t">Create a new version</div>
      <div class="nvd-s">
        Start fresh, or copy everything from an existing version.
      </div>
    </div>

    <div class="nvd-body">
      <div class="nvd-cards">
        <button
          type="button"
          class="nvd-card"
          [class.sel]="mode === 'new'"
          (click)="mode = 'new'"
        >
          <span class="nvd-ic">✦</span>
          <span class="nvd-card-t">Start empty</span>
          <span class="nvd-card-s"
            >A blank version — add projects, pipelines and the form from
            scratch.</span
          >
        </button>
        <button
          type="button"
          class="nvd-card"
          [class.sel]="mode === 'copy'"
          [disabled]="!availableVersion?.length"
          (click)="mode = 'copy'"
        >
          <span class="nvd-ic">⧉</span>
          <span class="nvd-card-t">Copy an existing version</span>
          <span class="nvd-card-s"
            >Duplicates its projects, pipelines, form and schedules.</span
          >
        </button>
      </div>

      <div class="nvd-list" *ngIf="mode === 'copy'">
        <div class="nvd-list-l">Copy from</div>
        <button
          type="button"
          class="nvd-row"
          *ngFor="let v of availableVersion"
          [class.sel]="selectedVersionId === v.version_id"
          (click)="selectedVersionId = v.version_id"
        >
          <span
            class="nvd-radio"
            [class.on]="selectedVersionId === v.version_id"
          ></span>
          <span class="nvd-vbadge">{{ v.version_name }}</span>
          <span class="nvd-stage" [ngClass]="stageClass(v.stage)">{{
            stageLabel(v.stage)
          }}</span>
          <span class="nvd-meta">{{
            v.updated_dtm | date : 'dd MMM yyyy'
          }}</span>
        </button>
      </div>
    </div>

    <div class="nvd-foot">
      <button type="button" class="btn-ghost" [mat-dialog-close]="false">
        Cancel
      </button>
      <button
        type="button"
        class="btn-primary"
        cdkFocusInitial
        [disabled]="!canCreate"
        [mat-dialog-close]="result"
      >
        {{ mode === 'copy' ? 'Create copy' : 'Create empty version' }}
      </button>
    </div>
  `,
  styleUrls: ['./new-version-dialog.component.scss'],
})
export class NewVersionDialogComponent implements OnInit {
  availableVersion: any;
  mode: 'new' | 'copy' = 'new';
  selectedVersionId = '';

  constructor(
    public dialogRef: MatDialogRef<NewVersionDialogComponent>,
    private solutionServices: SolutionsAppService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.solutionServices.getAvailableVersion(this.data['solution']).subscribe({
      next: (response: any) => {
        this.availableVersion = response;
      },
    });
  }

  get canCreate(): boolean {
    return this.mode === 'new' || !!this.selectedVersionId;
  }

  get result(): string {
    return this.mode === 'new' ? 'New' : this.selectedVersionId;
  }

  stageLabel(stage: string): string {
    switch (stage) {
      case 'staging':
        return 'Draft';
      case 'approved_for_production':
        return 'Approved';
      case 'production':
        return 'Live';
      default:
        return (stage || '').replace(/_/g, ' ');
    }
  }

  stageClass(stage: string): string {
    switch (stage) {
      case 'production':
        return 'st-green';
      case 'approved_for_production':
        return 'st-blue';
      default:
        return 'st-amber';
    }
  }
}
